/**
 * Transport-level knobs shared by both API clients. Kept apart from the
 * clients themselves so tests that mock `@/services/vnext-api` wholesale
 * don't take these down with it.
 */

/**
 * Default per-request timeout. A stalled API has to fail fast: an
 * unbounded wait is what let unguarded polls stack up and turn a stall
 * into a freeze (2026-08-25). Endpoints that legitimately run long opt
 * out per call with the two constants below.
 */
export const REQUEST_TIMEOUT_MS = 60_000;
/** Synchronous work that takes real time: LLM extraction/classification,
 * Gotenberg rendering, template validation, bulk creates, Freshdesk resync. */
export const LONG_REQUEST_TIMEOUT_MS = 120_000;
/** Placing a registry-document order: the API gives Armadillo up to 150 s
 * (four 30 s legs through edf) after a catalogue read of up to 60 s. The
 * browser has to outwait that, or a slow success reads as "outcome
 * unknown" while the order is in fact placed. */
export const REGISTRY_ORDER_TIMEOUT_MS = 240_000;
/** Multipart uploads are bounded by the file and the link, not by us. */
export const UPLOAD_TIMEOUT_MS = 0;

/** Per-call knobs a poller threads through — the `AbortSignal` that
 * `usePoll` fires on unmount, hide and supersede. */
export interface RequestOptions {
  signal?: AbortSignal;
}

/** `RequestOptions` for a list read that supports version-token polling:
 * pass the `etag` from the previous response and the request carries
 * `If-None-Match`; an unchanged set answers 304 with no body. Omit (or
 * pass `null`) to force a full fetch. */
export interface ConditionalRequestOptions extends RequestOptions {
  etag?: string | null;
}

/** A conditional list read's outcome: the payload with the `ETag` to send
 * back next time, or the literal `'not-modified'` for a 304 (the caller
 * keeps what it has — `usePoll` maps it to `UNCHANGED`). */
export type ConditionalResult<T> = { data: T; etag: string | null } | 'not-modified';

/** Parse a `Retry-After` header (delta-seconds or HTTP-date) into ms. */
export function parseRetryAfter(header: unknown): number | null {
  if (typeof header !== 'string' || header.trim() === '') {
    return null;
  }
  const seconds = Number(header);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }
  const at = Date.parse(header);
  return Number.isNaN(at) ? null : Math.max(0, at - Date.now());
}

/**
 * The API answered 503 (connection pool exhausted, upstream down). Kept
 * distinct from the generic "Server error" so pollers can back off — and
 * honour `Retry-After` — instead of treating it as a hard failure.
 */
export class ApiUnavailableError extends Error {
  readonly status = 503;
  readonly retryAfterMs: number | null;

  constructor(retryAfterMs: number | null) {
    super('Service unavailable (503)');
    this.name = 'ApiUnavailableError';
    this.retryAfterMs = retryAfterMs;
  }
}
